import { getAllMarginsForAllMarkets } from '$lib/data/userHoldings';
import {
	getClosePositionPostTradeDetails,
	getMargin,
	getShouldUpdateKeeperFee
} from '$lib/data/userInteract';
import {
	addErrorNotification,
	addTransactionNotification
} from '$lib/notifications/notificationManager';
import {
	closePositionInput,
	closePositionOrderType,
	closePositionPostTradeDetails,
	currentClosePosition,
	limitInputForClosePosition,
	popupModalOpen,
	popupModalType,
	triggerInputForClosePosition
} from '$lib/popups/popupsStore';
import type { MarketIdentifiers } from '$lib/types/market';
import type { Position } from '$lib/types/position';
import type { ActionType, OrderExecutionType } from '$lib/types/userInteract';
import { makeBodyScrollable, toBN } from '$lib/util/util';
import {
	addMarginLimitTradeSmartContract,
	closeFullLimitTradeSmartContract
} from '$lib/web3/actions/tradeLimit';
import {
	closeAllTradeSmartContract,
	closeFullTradeSmartContract,
	executeStandardOrder
} from '$lib/web3/actions/tradeStandard';
import type { SendTransactionResult } from '@wagmi/core';
import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import { get } from 'svelte/store';
import {
	currentMarket,
	currentMarketAsset,
	currentMarketIdentifier,
	tokensAllowedForDepositBalance,
	unsubmittedOrders,
	userBalances
} from '../store';
import { addUnsubmittedOrder, refreshUserHoldings } from '../userHoldings/userHoldings';
import {
	adjustMarginMarket,
	closingPosition,
	executingOrder,
	executionType,
	input,
	limitInputForTrade,
	limitOrderExpiryTimestamp,
	longOrShort,
	marginInputForTrade,
	marginsWithoutPositions,
	expiredOrders,
	priceImpactDelta,
	triggerInputForTrade,
	takeProfitInput,
	stopLossInput,
	isEntirePosition,
	isAddMaxMargin,
	activeSection
} from './store';
import { getParsedError } from '$lib/web3/errorHandling';
import { smartContractWalletAddress } from '../scwallet/store';
import {
	AUTOSWAP_OP,
	AUTOSWAP_STABLES,
	AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION
} from '$lib/settings';
import { amountPerTokenToSwap, matchaCallData } from '$lib/home/store';
import { findRequiredCallData, getStablesPrices } from '$lib/data/matchaCallData';
import { tokensAllowedForDeposit } from '../scwallet/config';
import { depositOrWithdrawPopupToken } from '$lib/popups/wallet/store';
import type { AdjustMarginTitleType } from '$lib/popups/Margin/adjustMarginTypes';
import type { TokenSymbol } from '$lib/types/tokens';
import { getUserTradingSeasonStats } from '$lib/data/rewards';
import { selectedSeasonIdentifier } from '$lib/tradingSeason/store';
import { ordersRaw } from '$store/orders';
import { logAnalytcisEvent } from '$lib/util/analytics';

export async function refreshUserInteract() {
	getMargin();
}

export async function executeMarketOrder() {
	executingOrder.set(true);
	let amount: string = '0';
	let currentMarketToTrade = get(currentMarket);
	if (currentMarketToTrade)
		amount = new BigNumber(get(input))
			.dividedBy(new BigNumber(currentMarketToTrade.currentMarketPrice))
			.multipliedBy(1e18)
			.toString();
	if (get(longOrShort) == 'Short') amount = '-' + amount;
	await executeOrderWithAmount(amount);
	executingOrder.set(false);
}

export async function executeLimitOrder() {
	executingOrder.set(true);
	let amount: string = '0';
	let currentMarketToTrade = get(currentMarket);
	if (currentMarketToTrade) {
		amount = new BigNumber(get(input))
			.dividedBy(new BigNumber(currentMarketToTrade.currentMarketPrice))
			.multipliedBy(1e18)
			.toString();
		if (get(longOrShort) == 'Short') amount = '-' + amount;

		const triggerPrice = get(triggerInputForTrade);
		const limitPrice = get(limitInputForTrade);
		const currentPrice = currentMarketToTrade.safePrice;
		let isUpper: boolean;
		if (new BigNumber(triggerPrice).gte(new BigNumber(currentPrice))) {
			isUpper = true;
			if (new BigNumber(limitPrice).lt(new BigNumber(triggerPrice))) {
				executingOrder.set(false);
				return 'Invalid trigger and limit price';
			}
		} else if (new BigNumber(triggerPrice).lte(new BigNumber(currentPrice))) {
			isUpper = false;
			if (new BigNumber(limitPrice).gt(new BigNumber(triggerPrice))) {
				executingOrder.set(false);
				return 'Invalid trigger and limit price';
			}
		} else {
			executingOrder.set(false);
			return 'Invalid trigger and limit price';
		}

		await executeLimitOrderWithAmount(amount, limitPrice, triggerPrice, isUpper);
	}

	executingOrder.set(false);
}

export async function executeOrder() {
	const execType: OrderExecutionType = get(executionType);
	logAnalytcisEvent('click_execute_order', { 'order-type': execType });
	if (execType == 'Market') {
		await executeMarketOrder();
	} else {
		await executeLimitOrder();
	}

	// Adding this because xp gets calculated after every execute order
	await getUserTradingSeasonStats(get(selectedSeasonIdentifier), get(smartContractWalletAddress));
}

export async function executeOrderWithAmount(amount: string) {
	try {
		if (get(currentMarket)) {
			let margin = get(isAddMaxMargin)
				? ethers.constants.MaxUint256.toString()
				: get(marginInputForTrade);
			let orderAmount: string = new BigNumber(amount).toFixed(0);
			let takeProfit = toBN(get(takeProfitInput)).toString();
			let stopLoss = toBN(get(stopLossInput)).toString();
			const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
			if (get(AUTOSWAP_STABLES))
				await findRequiredCallData(
					get(tokensAllowedForDepositBalance),
					new BigNumber(get(marginInputForTrade))
				);
			let tx: SendTransactionResult = await executeStandardOrder(
				margin,
				orderAmount,
				get(limitOrderExpiryTimestamp),
				get(currentMarketIdentifier),
				get(priceImpactDelta),
				get(marginsWithoutPositions),
				get(tokensAllowedForDepositBalance),
				get(expiredOrders),
				takeProfit,
				stopLoss,
				get(isEntirePosition),
				get(smartContractWalletAddress) as `0x${string}`,
				get(AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION),
				shouldUpdateKeeperFee,
				get(amountPerTokenToSwap),
				get(matchaCallData),
				get(AUTOSWAP_STABLES),
				get(AUTOSWAP_OP),
				get(tokensAllowedForDeposit)
			);
			isAddMaxMargin.set(false);
			input.set('0');
			// resetting these for next transaction
			matchaCallData.set({});
			amountPerTokenToSwap.set({});
			addTransactionNotification(
				'Transaction Confirmed',
				'Your order has been submitted.',
				tx,
				() => {
					updateData();
				}
			);
			addUnsubmittedOrder(
				get(currentMarketIdentifier),
				get(currentMarketAsset) as string,
				orderAmount,
				get(smartContractWalletAddress) as `0x${string}`,
				tx
			);

			// Adding this because xp gets calculated after every execute order
			await getUserTradingSeasonStats(
				get(selectedSeasonIdentifier),
				get(smartContractWalletAddress)
			);
		} else {
			throw {
				name: 'Failed to load data',
				message: 'Please refresh the page and try again.'
			};
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		console.log(e);
		addErrorNotification(title, description, JSON.stringify(e, Object.getOwnPropertyNames(e)));
	}
}

export async function executeLimitOrderWithAmount(
	amount: string,
	limitPrice: string,
	triggerPrice: string,
	isUpper: boolean
) {
	try {
		if (get(currentMarket) != undefined) {
			let orderAmount: string = new BigNumber(amount).toFixed(0).toString();
			limitPrice = new BigNumber(limitPrice).toFixed(0).toString();
			triggerPrice = new BigNumber(triggerPrice).toFixed(0).toString();
			let marginInput = get(marginInputForTrade);
			let margin = get(isAddMaxMargin) ? ethers.constants.MaxUint256.toString() : marginInput;

			let takeProfit = toBN(get(takeProfitInput)).toString();
			let stopLoss = toBN(get(stopLossInput)).toString();

			const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
			if (get(AUTOSWAP_STABLES))
				await findRequiredCallData(
					get(tokensAllowedForDepositBalance),
					new BigNumber(get(marginInputForTrade))
				);
			let tx: SendTransactionResult = await addMarginLimitTradeSmartContract(
				margin,
				orderAmount,
				limitPrice,
				triggerPrice,
				takeProfit,
				stopLoss,
				get(limitOrderExpiryTimestamp),
				get(currentMarketIdentifier),
				get(priceImpactDelta),
				get(marginsWithoutPositions),
				get(tokensAllowedForDepositBalance),
				get(expiredOrders),
				get(smartContractWalletAddress) as `0x${string}`,
				get(AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION),
				shouldUpdateKeeperFee,
				get(amountPerTokenToSwap),
				get(matchaCallData),
				get(AUTOSWAP_STABLES),
				get(AUTOSWAP_OP),
				get(tokensAllowedForDeposit)
			);
			matchaCallData.set({});
			amountPerTokenToSwap.set({});
			input.set('0');
			addTransactionNotification(
				'Transaction Confirmed',
				'Your Limit order has been submitted.',
				tx,
				() => {
					updateData();
				}
			);

			// Adding this because xp gets calculated after every execute order
			await getUserTradingSeasonStats(
				get(selectedSeasonIdentifier),
				get(smartContractWalletAddress)
			);
		} else {
			throw {
				name: 'Failed to load data',
				message: 'Please refresh the page and try again.'
			};
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
	}
}

export async function closePosition(tpslOrderId?: string) {
	closingPosition.set(true);

	// force call to getClosePositionPostTradeDetails
	if (!get(closePositionPostTradeDetails)) {
		const _ = await getClosePositionPostTradeDetails();
	}
	let amount: string = get(closePositionInput);
	let amountBN = new BigNumber(amount);
	let _currentClosePosition = get(currentClosePosition);
	let currentClosePositionSizeBN = new BigNumber(_currentClosePosition.size);

	if (amount === undefined || _currentClosePosition === undefined) {
		throw {
			name: 'Failed to load data',
			message: 'Please refresh the page and try again.'
		};
	}

	if (amountBN.eq(currentClosePositionSizeBN)) {
		if (get(closePositionOrderType) == 'Limit') {
			await fullyLimitClosePosition(_currentClosePosition);
		} else {
			await fullyClosePosition(_currentClosePosition);
		}
	} else {
		if (currentClosePositionSizeBN.gt(0)) {
			amount = '-' + amount;
		}
		if (get(closePositionOrderType) == 'Limit') {
			await executeLimitCloseOrderWithAmount(amount);
		} else {
			await executeCloseOrderWithAmount(amount);
		}
	}

	closingPosition.set(false);
	popupModalOpen.set(false);
	makeBodyScrollable();
}

export function updateData() {
	getStablesPrices(get(userBalances));
	for (let i = 0; i < 5; i++) {
		setTimeout(() => {
			refreshUserInteract();
			refreshUserHoldings();
			ordersRaw.apiCall({});
			getAllMarginsForAllMarkets();
		}, 4000 + 3000 * i);
	}
}

export async function fullyClosePosition(_currentClosePosition: Position) {
	try {
		const scw = get(smartContractWalletAddress) as `0x${string}`;
		const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
		let tx: SendTransactionResult = await closeFullTradeSmartContract(
			_currentClosePosition.marketIdentifier,
			get(priceImpactDelta),
			scw,
			shouldUpdateKeeperFee
		);

		addTransactionNotification('Transaction Confirmed', 'Your order has been submitted', tx, () => {
			updateData();
		});

		addUnsubmittedOrder(
			_currentClosePosition.marketIdentifier,
			_currentClosePosition.asset as string,
			_currentClosePosition.size,
			scw,
			tx
		);
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
	}
}

export async function fullyLimitClosePosition(_currentClosePosition: Position) {
	try {
		const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();

		let tx: SendTransactionResult = await closeFullLimitTradeSmartContract(
			_currentClosePosition.marketIdentifier,
			get(priceImpactDelta),
			new BigNumber(get(triggerInputForClosePosition))
				.multipliedBy(1e18)
				.gt(new BigNumber(get(closePositionPostTradeDetails).assetPrice)),
			get(triggerInputForClosePosition),
			get(limitInputForClosePosition),
			get(limitOrderExpiryTimestamp),
			get(smartContractWalletAddress) as `0x${string}`,
			shouldUpdateKeeperFee
		);
		addTransactionNotification('Transaction Confirmed', 'Your order has been submitted', tx, () => {
			updateData();
		});
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
	}
}

export async function executeCloseOrderWithAmount(amount: string) {
	try {
		if (get(currentClosePosition) != undefined) {
			let orderAmount: string = new BigNumber(amount).toFixed(0).toString();
			const scw = get(smartContractWalletAddress) as `0x${string}`;
			const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
			let tx: SendTransactionResult = await executeStandardOrder(
				'0', // margin input in closing positions is 0 so margin not removed from other markets
				orderAmount,
				get(limitOrderExpiryTimestamp),
				get(currentClosePosition).marketIdentifier,
				get(priceImpactDelta),
				get(marginsWithoutPositions),
				get(tokensAllowedForDepositBalance),
				get(expiredOrders),
				'0',
				'0',
				false,
				scw,
				get(AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION),
				shouldUpdateKeeperFee,
				get(amountPerTokenToSwap),
				get(matchaCallData),
				get(AUTOSWAP_STABLES),
				get(AUTOSWAP_OP),
				get(tokensAllowedForDeposit)
			);
			input.set('0');
			// resetting these for next transaction
			matchaCallData.set({});
			amountPerTokenToSwap.set({});
			addTransactionNotification(
				'Transaction Confirmed',
				'Your order has been submitted.',
				tx,
				() => {
					updateData();
				}
			);
			addUnsubmittedOrder(
				get(currentClosePosition).marketIdentifier,
				get(currentClosePosition).asset as string,
				amount,
				scw,
				tx
			);
		} else {
			throw {
				name: 'Failed to load data',
				message: 'Please refresh the page and try again.'
			};
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
	}
}

export async function executeLimitCloseOrderWithAmount(amount: string) {
	try {
		if (get(currentClosePosition) != undefined) {
			let orderAmount: string = new BigNumber(amount).toFixed(0).toString();
			const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
			let tx: SendTransactionResult = await addMarginLimitTradeSmartContract(
				'0',
				orderAmount,
				toBN(get(limitInputForClosePosition)).toString(),
				toBN(get(triggerInputForClosePosition)).toString(),
				get(takeProfitInput),
				get(stopLossInput),
				get(limitOrderExpiryTimestamp),
				get(currentClosePosition).marketIdentifier,
				get(priceImpactDelta),
				get(marginsWithoutPositions),
				get(tokensAllowedForDepositBalance),
				get(expiredOrders),
				get(smartContractWalletAddress) as `0x${string}`,
				get(AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION),
				shouldUpdateKeeperFee,
				get(amountPerTokenToSwap),
				get(matchaCallData),
				get(AUTOSWAP_STABLES),
				get(AUTOSWAP_OP),
				get(tokensAllowedForDeposit)
			);
			matchaCallData.set({});
			amountPerTokenToSwap.set({});
			input.set('0');
			addTransactionNotification(
				'Transaction Confirmed',
				'Your order has been submitted.',
				tx,
				() => {
					updateData();
				}
			);
		} else {
			throw {
				name: 'Failed to load data',
				message: 'Please refresh the page and try again.'
			};
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
	}
}

export function openAdjustMarginModal(
	marketId: MarketIdentifiers = get(currentMarketIdentifier),
	active: AdjustMarginTitleType = 'Deposit'
) {
	popupModalType.set('AdjustMargin');
	popupModalOpen.set(true);
	adjustMarginMarket.set(marketId);
	activeSection.set(active);
}

export function openTokenDepositModal(token: TokenSymbol = 'sUSD') {
	popupModalOpen.set(true);
	popupModalType.set('DepositWallet');
	depositOrWithdrawPopupToken.set(token);
}
export function openTokenWithdrawModal(token: TokenSymbol = 'sUSD') {
	popupModalOpen.set(true);
	popupModalType.set('WithdrawWallet');
	depositOrWithdrawPopupToken.set(token);
}

export function openViewAllTokenModal() {
	popupModalOpen.set(true);
	popupModalType.set('ViewAllTokenBalances');
}

export function openViewAllMarginModal() {
	popupModalOpen.set(true);
	popupModalType.set('ViewAllMarginBalances');
}

export function getTradeStatus(status: string, action: ActionType = 'openPosition') {
	if (action === 'closePosition' && status === 'CanLiquidate') {
		return 'Position Liquidated';
	}
	return status.replace(/([A-Z]+)/g, ' $1').replace(/([A-Z][a-z])/g, ' $1');
}

export async function fullyCloseAllPositions(_currentClosePositions: Position[]) {
	try {
		const marketsToClose = _currentClosePositions.map((position) => position.marketIdentifier);
		const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
		const scw = get(smartContractWalletAddress) as `0x${string}`;

		let tx: SendTransactionResult = await closeAllTradeSmartContract(
			marketsToClose,
			get(priceImpactDelta),
			scw,
			shouldUpdateKeeperFee
		);

		addTransactionNotification('Transaction Confirmed', 'Your order has been submitted', tx, () => {
			updateData();
		});

		for (let i = 0; i < _currentClosePositions.length; i++) {
			addUnsubmittedOrder(
				_currentClosePositions[i].marketIdentifier,
				_currentClosePositions[i].asset as string,
				_currentClosePositions[i].size,
				scw,
				tx
			);
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
	}
}
